/**
 * Servizio per interagire con l'API di Telegram Web App
 */

// Definiamo l'interfaccia per Telegram Web App
export interface TelegramWebApp {
  ready: () => void;
  initData: string;
  initDataUnsafe?: {
    user?: TelegramUser;
    auth_date?: number;
    hash?: string;
    start_param?: string;
    chat_instance?: string;
    chat_type?: string;
    query_id?: string;
  };
  expand: () => void;
  setHeaderColor?: (color: string) => void;
  enableClosingConfirmation?: () => void;
  BackButton: {
    isVisible: boolean;
    onClick: (callback: Function) => void;
    offClick: (callback: Function) => void;
    show: () => void;
    hide: () => void;
  };
  isExpanded?: boolean;
  viewportHeight?: number;
  viewportStableHeight?: number;
  headerColor?: string;
  backgroundColor?: string;
  setBackgroundColor?: (color: string) => void;
  onEvent?: (eventType: string, eventHandler: Function) => void;
  offEvent?: (eventType: string, eventHandler: Function) => void;
  requestFullScreen?: () => void; // Disponibile dalla versione 8.0
  setupSwipeBackBehavior?: (params: { allow_vertical_swipe: boolean }) => void; // Disponibile dalla v7.7
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (callback: Function) => void;
  }
  // Altre proprietà e metodi che potrebbero essere necessari
  version?: string;
  platform?: string;
  colorScheme?: string;
  themeParams?: Record<string, any>;
  showPopup?: (params: {
    title?: string;
    message: string;
    buttons?: Array<{
      id?: string;
      type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive';
      text?: string;
    }>;
  }, callback?: (buttonId: string) => void) => void;
}

export interface TelegramUser {
  id?: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  photo_url?: string;
  is_premium?: boolean;
  // Proprietà aggiuntiva per gestire utenti che richiedono registrazione
  needsRegistration?: boolean;
  // Proprietà per la timezone
  timezone?: string;
  timezoneOffset?: number;
}

// Verifica se siamo su Telegram Desktop (per evitare espansione automatica)
export const isTelegramDesktop = (): boolean => {
  try {
    const webApp = typeof window !== 'undefined' && window.Telegram?.WebApp;
    if (!webApp) return false;

    // La proprietà platform indica il client Telegram
    // tdesktop = Telegram Desktop, macos = Telegram per macOS
    const platform = webApp.platform?.toLowerCase() || '';
    const isDesktop = platform === 'tdesktop' ||
                      platform === 'macos' ||
                      platform === 'linux' ||
                      platform === 'windows';

    console.log(`[TELEGRAM] Platform rilevata: "${platform}", isDesktop: ${isDesktop}`);
    return isDesktop;
  } catch (error) {
    console.error('[TELEGRAM] Errore nel rilevare desktop:', error);
    return false;
  }
};

// Verifica se l'app è in esecuzione all'interno di Telegram
export const isTelegramWebApp = (): boolean => {
  // VERIFICA CRITICA: initData esiste SOLO quando l'app è aperta da Telegram
  // L'oggetto WebApp viene creato anche nel browser normale dallo script telegram-web-app.js
  const hasValidInitData = typeof window !== 'undefined' &&
                           typeof window.Telegram !== 'undefined' &&
                           typeof window.Telegram.WebApp !== 'undefined' &&
                           typeof window.Telegram.WebApp.initData === 'string' &&
                           window.Telegram.WebApp.initData.length > 0;

  // Verifica aggiuntiva per URL Telegram (queste stringhe appaiono negli URL Telegram)
  const hasWebAppQueryParam = typeof window !== 'undefined' &&
                              (window.location.href.includes('tgwebapp=') ||
                               window.location.search.includes('tgwebapp') ||
                               window.location.hostname.includes('t.me') ||
                               window.location.hostname.includes('telegram'));

  // Verifica WebView proxy (utilizzato per le comunicazioni tra WebApp e Telegram)
  const hasTelegramProxy = typeof window !== 'undefined' &&
                           typeof (window as any).TelegramWebviewProxy !== 'undefined';

  // Verifica basata su user agent (ulteriore controllo)
  const hasTelegramUserAgent = typeof window !== 'undefined' &&
                              typeof navigator !== 'undefined' &&
                              navigator.userAgent &&
                              (navigator.userAgent.includes('Telegram') ||
                               navigator.userAgent.includes('TelegramBot') ||
                               navigator.userAgent.includes('TelegramWebApp'));

  // Verifica parametri speciali negli URL delle WebApp Telegram
  const hasSpecificTelegramParams = typeof window !== 'undefined' &&
                                   window.location.search &&
                                   (window.location.search.includes('tgWebAppData=') ||
                                    window.location.search.includes('tgWebAppVersion=') ||
                                    window.location.search.includes('tgWebAppPlatform='));

  // ⭐ FIX iOS: Verifica presenza oggetto WebApp anche senza initData
  // Su iOS/Safari, initData può essere vuoto ma l'oggetto WebApp esiste se siamo in Telegram
  const hasWebAppObject = typeof window !== 'undefined' &&
                         typeof window.Telegram !== 'undefined' &&
                         typeof window.Telegram.WebApp !== 'undefined' &&
                         typeof window.Telegram.WebApp.ready === 'function';

  // ⭐ FIX iOS: Verifica se siamo in modalità standalone (iOS WebApp)
  const isStandalone = typeof window !== 'undefined' &&
                      (window.navigator as any).standalone === true;

  // ⭐ FIX: Verifica initDataUnsafe che contiene i dati utente anche quando initData è vuoto
  // Questo è un fallback importante per alcuni client Telegram
  const hasInitDataUnsafe = typeof window !== 'undefined' &&
                            typeof window.Telegram !== 'undefined' &&
                            typeof window.Telegram.WebApp !== 'undefined' &&
                            window.Telegram.WebApp.initDataUnsafe &&
                            window.Telegram.WebApp.initDataUnsafe.user &&
                            window.Telegram.WebApp.initDataUnsafe.user.id;

  // ⭐ FIX: Verifica platform che è settato solo in Telegram
  const hasPlatform = typeof window !== 'undefined' &&
                      typeof window.Telegram !== 'undefined' &&
                      typeof window.Telegram.WebApp !== 'undefined' &&
                      window.Telegram.WebApp.platform &&
                      window.Telegram.WebApp.platform !== 'unknown';

  // ⭐ FIX Telegram Desktop: Verifica se abbiamo una versione valida (solo in Telegram)
  const hasVersion = typeof window !== 'undefined' &&
                     typeof window.Telegram !== 'undefined' &&
                     typeof window.Telegram.WebApp !== 'undefined' &&
                     window.Telegram.WebApp.version &&
                     window.Telegram.WebApp.version.length > 0;

  // ⭐ FIX Telegram Desktop: Verifica colorScheme (solo settato in Telegram)
  const hasColorScheme = typeof window !== 'undefined' &&
                         typeof window.Telegram !== 'undefined' &&
                         typeof window.Telegram.WebApp !== 'undefined' &&
                         window.Telegram.WebApp.colorScheme &&
                         (window.Telegram.WebApp.colorScheme === 'light' || window.Telegram.WebApp.colorScheme === 'dark');

  // ⭐ FIX Telegram Desktop: Verifica themeParams (oggetto con colori del tema)
  const hasThemeParams = typeof window !== 'undefined' &&
                         typeof window.Telegram !== 'undefined' &&
                         typeof window.Telegram.WebApp !== 'undefined' &&
                         window.Telegram.WebApp.themeParams &&
                         typeof window.Telegram.WebApp.themeParams === 'object' &&
                         Object.keys(window.Telegram.WebApp.themeParams).length > 0;

  // ⚠️ CRITICAL FIX: Lo script telegram-web-app.js crea SEMPRE l'oggetto WebApp
  // con version, colorScheme, themeParams anche FUORI da Telegram!
  // L'UNICO modo affidabile per sapere se siamo IN Telegram è:
  // 1. initData non vuoto (hasValidInitData)
  // 2. initDataUnsafe.user.id presente (hasInitDataUnsafe)
  // 3. TelegramWebviewProxy presente (hasTelegramProxy) - solo in WebView nativo
  // 4. User agent contiene "Telegram" (hasTelegramUserAgent)
  // 5. URL contiene parametri specifici Telegram (hasWebAppQueryParam, hasSpecificTelegramParams)

  // NON usare: hasVersion, hasColorScheme, hasThemeParams, hasWebAppObject, hasPlatform
  // perché sono settati dallo script anche fuori da Telegram!

  const isInTelegram = hasValidInitData ||
                       hasInitDataUnsafe ||
                       hasTelegramProxy ||
                       hasTelegramUserAgent ||
                       hasWebAppQueryParam ||
                       hasSpecificTelegramParams;

  // Nessun override: solo accesso da Telegram reale
  const finalResult = !!isInTelegram;

  // Verbose logging per debug in tutti gli ambienti
  console.log('[TelegramService] Verifica ambiente Telegram:', {
    isInTelegram: finalResult,
    host: typeof window !== 'undefined' ? window.location.hostname : 'no-window',
    hasValidInitData,
    initDataLength: typeof window !== 'undefined' && window.Telegram?.WebApp?.initData ? window.Telegram.WebApp.initData.length : 0,
    hasWebAppQueryParam,
    hasTelegramProxy,
    hasTelegramUserAgent,
    hasSpecificTelegramParams,
    hasInitDataUnsafe,
    hasPlatform,
    hasVersion,
    hasColorScheme,
    hasThemeParams,
    platform: typeof window !== 'undefined' && window.Telegram?.WebApp?.platform ? window.Telegram.WebApp.platform : 'no-platform',
    version: typeof window !== 'undefined' && window.Telegram?.WebApp?.version ? window.Telegram.WebApp.version : 'no-version',
    colorScheme: typeof window !== 'undefined' && window.Telegram?.WebApp?.colorScheme ? window.Telegram.WebApp.colorScheme : 'no-colorScheme',
  });

  return finalResult;
};

// Helper per accedere in sicurezza all'oggetto WebApp
export const getTelegramWebApp = (): TelegramWebApp | null => {
  if (!isTelegramWebApp()) return null;
  return window.Telegram?.WebApp as unknown as TelegramWebApp;
};

// Helper per verificare compatibilità versione
export const getTelegramVersion = (): number => {
  const webApp = getTelegramWebApp();
  if (!webApp || !webApp.version) {
    console.log('[TelegramService] Versione WebApp non disponibile, usando fallback 6.0');
    return 6.0; // versione di fallback
  }
  const version = parseFloat(webApp.version);
  console.log(`[TelegramService] Versione Telegram WebApp: ${version}`);

  // Verifica supporto DeviceStorage
  if (version < 9.0) {
    console.warn(`[TelegramService] DeviceStorage non supportato in versione ${version} (richiesta v9.0+)`);
  }

  return version;
};

// Helper per verificare se una funzionalità è supportata
export const isFeatureSupported = (feature: string): boolean => {
  const version = getTelegramVersion();
  const requirements: Record<string, number> = {
    'BackButton': 6.1,
    'setHeaderColor': 6.1,
    'enableClosingConfirmation': 6.2,
    'setupSwipeBackBehavior': 7.7,
    'requestFullScreen': 8.0,
    'DeviceStorage': 9.0
  };

  const isSupported = version >= (requirements[feature] || 6.0);

  if (!isSupported && feature === 'DeviceStorage') {
    console.warn(`[TelegramService] ${feature} non supportato (v${version} < v${requirements[feature]}). Verrà usato localStorage.`);
  }

  return isSupported;
};

// Helper per inviare eventi direttamente a Telegram WebApp
const postTelegramEvent = (eventName: string, data: any = {}): void => {
  if (!isTelegramWebApp()) return;
  try {
    // Accediamo all'oggetto di evento direttamente
    const win = window as any;
    if (win.TelegramWebviewProxy) {
      win.TelegramWebviewProxy.postEvent(eventName, JSON.stringify(data));
      console.log(`Evento postato a Telegram: ${eventName}`);
    } else {
      console.debug(`TelegramWebviewProxy non disponibile per inviare: ${eventName}`);
    }
  } catch (error) {
    console.error(`Errore nell'inviare evento ${eventName}:`, error);
  }
};

// Ottiene i dati di inizializzazione di Telegram
export const getTelegramInitData = (): string | null => {
  const webApp = getTelegramWebApp();
  return webApp ? webApp.initData : null;
};

// Espande la WebApp a tutto schermo e nasconde l'header
// Richiede la modalità fullscreen
export const requestFullScreen = (): void => {
  try {
    // Tentiamo prima l'approccio diretto con postEvent
    postTelegramEvent('web_app_request_fullscreen');
    console.log('Richiesto fullscreen tramite evento diretto web_app_request_fullscreen');

    // Poi come fallback proviamo l'API di alto livello (se disponibile)
    const webApp = getTelegramWebApp();
    if (webApp && webApp.requestFullScreen) {
      console.log('Richiesta fullscreen con API di alto livello (v8.0+)');
      webApp.requestFullScreen();
    }

    // Infine usiamo expand (sempre disponibile)
    if (webApp && webApp.expand) {
      webApp.expand();
    }
  } catch (error) {
    console.error('Errore nel richiedere fullscreen:', error);
  }
};

// Queste tre nuove funzioni ci aiutano a controllare meglio l'UI di Telegram
export const hideHeader = (): void => {
  try {
    // Approccio diretto con evento personalizzato
    postTelegramEvent('web_app_hide_header');

    // Approccio con CSS - cerchiamo di nascondere gli elementi dell'header di Telegram
    if (typeof document !== 'undefined') {
      // Aggiungiamo uno stile globale per nascondere gli elementi dell'header di Telegram
      const style = document.createElement('style');
      style.id = 'telegram-ui-hider';
      style.innerHTML = `
        /* Nasconde elementi specifici dell'UI di Telegram */
        .telegram-header,
        .tgme_header,
        .tgme_page_title,
        .tgme_toolbar,
        .tg-header,
        .tg-navbar,
        header[class*="tg"],
        nav[class*="tg"],
        div[class*="tg-navbar"],
        div[class*="tg-header"],
        div[class*="Navbar"],
        div[class*="Header"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
          height: 0 !important;
          z-index: -999 !important;
          position: absolute !important;
          transform: translateY(-200%) !important;
        }

        /* Assicuriamo che il nostro contenuto abbia priorità più alta */
        [data-telegram-ui="overlay"],
        .telegram-overlay,
        .telegram-sheet-content,
        .sheet-content-telegram {
          z-index: 99999 !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
        }
      `;
      document.head.appendChild(style);

      // Proviamo anche a trovare e nascondere elementi specifici (backup)
      const hideElements = (selector: string) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.display = 'none';
            el.style.visibility = 'hidden';
            el.style.opacity = '0';
            el.style.zIndex = '-999';
          }
        });
      };

      // Selettori comuni per elementi dell'UI di Telegram
      [
        '.telegram-header', '.tgme_header', '.tg-header', '.tg-navbar',
        'header', 'nav', 'header[class*="tg"]', 'nav[class*="tg"]',
        'div[class*="tg-navbar"]', 'div[class*="tg-header"]',
        'div[class*="Navbar"]', 'div[class*="Header"]'
      ].forEach(hideElements);
    }

    // Imposta colore di sfondo per mascherare l'header
    const webApp = getTelegramWebApp();
    if (webApp && webApp.setBackgroundColor) {
      webApp.setBackgroundColor('#0f1621'); // Colore scuro per il background
    }
  } catch (error) {
    console.error('Errore nel nascondere header Telegram:', error);
  }
};

// Imposta il modo in cui Telegram gestisce i gesti di swipe
export const disableSwipeToBack = (): void => {
  try {
    const webApp = getTelegramWebApp();
    if (webApp && webApp.setupSwipeBackBehavior) {
      webApp.setupSwipeBackBehavior({ allow_vertical_swipe: false });
      console.log('Disabilitato swipe-to-back in Telegram WebApp');
    }
  } catch (error) {
    console.error('Errore nel configurare swipe behavior:', error);
  }
};

// Metodo combinato per usare tutte le tecniche per ottenere fullscreen
// NOTA: Su Telegram Desktop questa funzione non fa nulla per mantenere la webview ridotta
export const enterImmersiveMode = (): void => {
  try {
    // Su desktop non entriamo in modalità immersiva
    if (isTelegramDesktop()) {
      console.log('[TELEGRAM] Desktop rilevato - modalità immersiva non attivata');
      return;
    }

    console.log('🔄 Attivazione modalità immersiva Telegram...');

    // 1. Blocca l'orientamento in verticale
    lockOrientation();

    // 2. Disabilita swipe per evitare uscite accidentali
    disableSwipeToBack();

    // 3. Richiedi fullscreen
    requestFullScreen();

    // 4. Nascondi header e altri elementi UI
    hideHeader();

    // 5. Previeni eventi drag indesiderati
    preventDragEvents();

    // 6. Attiva protezione screenshot
    setupScreenshotProtection();

    // 7. Registra handler per mantenere l'espansione (solo mobile)
    const webApp = getTelegramWebApp();
    if (webApp && webApp.onEvent) {
      webApp.onEvent('viewportChanged', () => {
        // Ri-espandi se la viewport cambia (solo se non desktop)
        if (webApp.expand && !isTelegramDesktop()) {
          webApp.expand();
        }
      });
    }

    console.log('✅ Modalità immersiva Telegram attivata');
  } catch (error) {
    console.error('❌ Errore nell\'attivare modalità immersiva:', error);
  }
};

// Previene eventi drag indesiderati per migliorare l'esperienza mobile
const preventDragEvents = (): void => {
  if (typeof document === 'undefined') return;

  try {
    // Previene drag di immagini e altri elementi
    document.addEventListener('dragstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
    }, { passive: false });

    // Previene selezioni accidentali durante il drag
    document.addEventListener('selectstart', (e) => {
      if ((e.target as HTMLElement)?.tagName === 'IMG') {
        e.preventDefault();
      }
    }, { passive: false });

    console.log('🔒 Prevenzione eventi drag attivata');
  } catch (error) {
    console.debug('Errore nella prevenzione eventi drag:', error);
  }
};

// Protegge il contenuto quando viene rilevato uno screenshot
export const setupScreenshotProtection = (): void => {
  try {
    const webApp = getTelegramWebApp();
    if (!webApp) {
      console.debug('[SCREENSHOT] WebApp non disponibile');
      return;
    }

    // Crea un overlay blur che copre tutto lo schermo
    let blurOverlay: HTMLDivElement | null = null;

    const createBlurOverlay = () => {
      if (blurOverlay) return;

      blurOverlay = document.createElement('div');
      blurOverlay.id = 'screenshot-protection-overlay';
      blurOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(15, 22, 33, 0.98);
        backdrop-filter: blur(50px);
        -webkit-backdrop-filter: blur(50px);
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.1s ease-in-out;
        pointer-events: none;
      `;

      // Testo informativo
      const message = document.createElement('div');
      message.style.cssText = `
        color: white;
        font-size: 18px;
        text-align: center;
        padding: 20px;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
      `;
      message.textContent = '🔒 Screenshot rilevato';

      blurOverlay.appendChild(message);
      document.body.appendChild(blurOverlay);

      // Fade in
      setTimeout(() => {
        if (blurOverlay) {
          blurOverlay.style.opacity = '1';
          message.style.opacity = '1';
        }
      }, 10);
    };

    const removeBlurOverlay = () => {
      if (!blurOverlay) return;

      // Fade out
      blurOverlay.style.opacity = '0';

      setTimeout(() => {
        if (blurOverlay && blurOverlay.parentNode) {
          blurOverlay.parentNode.removeChild(blurOverlay);
          blurOverlay = null;
        }
      }, 150);
    };

    // Ascolta l'evento di screenshot da Telegram
    if (webApp.onEvent) {
      webApp.onEvent('writeAccessRequested', () => {
        console.log('[SCREENSHOT] 📸 Screenshot rilevato - oscurando contenuto');
        createBlurOverlay();

        // Rimuovi l'overlay dopo 800ms
        setTimeout(() => {
          removeBlurOverlay();
        }, 800);
      });
    }

    // Backup: listener nativo per screenshot (Android)
    if (typeof window !== 'undefined') {
      window.addEventListener('blur', () => {
        // Potrebbe essere uno screenshot
        createBlurOverlay();
        setTimeout(() => {
          removeBlurOverlay();
        }, 500);
      });
    }

    // Backup: Rilevamento tramite Visibility API
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          createBlurOverlay();
          setTimeout(() => {
            removeBlurOverlay();
          }, 500);
        }
      });
    }

    console.log('✅ Protezione screenshot attivata');
  } catch (error) {
    console.error('❌ Errore nell\'attivare protezione screenshot:', error);
  }
};

// Disabilita lo swipe verticale per evitare la chiusura dell'app (disponibile dalla v7.7)
export const disableVerticalSwipe = (): void => {
  try {
    // Tentiamo prima l'approccio diretto con postEvent
    postTelegramEvent('web_app_setup_swipe_behavior', { allow_vertical_swipe: false });
    console.log('Disabilitato swipe verticale tramite evento diretto web_app_setup_swipe_behavior');

    // Poi come fallback proviamo l'API di alto livello (se disponibile)
    const webApp = getTelegramWebApp();
    if (webApp && webApp.setupSwipeBackBehavior) {
      console.log('Disabilitato swipe verticale con API di alto livello (v7.7+)');
      webApp.setupSwipeBackBehavior({ allow_vertical_swipe: false });
    }
  } catch (error) {
    console.error('Errore nel disabilitare lo swipe verticale:', error);
  }
};

export const expandApp = (): void => {
  const webApp = getTelegramWebApp();
  if (webApp) {
    try {
      // Su Telegram Desktop NON espandiamo automaticamente per mantenere la webview ridotta
      const isDesktop = isTelegramDesktop();

      if (isDesktop) {
        console.log('[TELEGRAM] Desktop rilevato - NON espando automaticamente la webapp');
      } else {
        // Solo su mobile: espandiamo l'app e richiediamo fullscreen
        console.log('[TELEGRAM] Mobile rilevato - espando la webapp');
        webApp.expand();
        requestFullScreen();

        // Disabilitiamo lo swipe verticale per evitare chiusure accidentali (solo mobile)
        disableVerticalSwipe();
      }

      // Inizializziamo il sistema di viewport dinamico (sempre, sia mobile che desktop)
      initTelegramViewportHandler();

      // Verifica la versione prima di usare funzionalità avanzate
      const version = webApp.version ? parseFloat(webApp.version) : 6.0;
      console.log(`Telegram WebApp versione rilevata: ${version}`);

      try {
        // setHeaderColor disponibile dalla versione 6.1+
        // NOTA: Telegram non accetta 'transparent', solo colori validi come '#RRGGBB'
        if (version >= 6.1 && webApp.setHeaderColor) {
          // Usa il colore di sfondo di default invece di transparent
          // per evitare errori "Invalid color format"
          const bgColor = webApp.backgroundColor || '#1a1a2e';
          webApp.setHeaderColor(bgColor);
          console.log('Header color impostato:', bgColor);
        } else {
          console.debug(`setHeaderColor non supportato nella versione ${version} (richiede 6.1+)`);
        }
      } catch (headerError) {
        console.debug('setHeaderColor non disponibile:', headerError);
      }

      try {
        // enableClosingConfirmation disponibile dalla versione 6.2+
        if (version >= 6.2 && webApp.enableClosingConfirmation) {
          webApp.enableClosingConfirmation();
          console.log('Conferma chiusura abilitata');
        } else {
          console.debug(`enableClosingConfirmation non supportato nella versione ${version} (richiede 6.2+)`);
        }
      } catch (confirmError) {
        console.debug('enableClosingConfirmation non disponibile:', confirmError);
      }

      // Debug informazioni sulla versione
      if (webApp.version) {
        console.log(`Versione Telegram WebApp: ${webApp.version}`);
      }
    } catch (e) {
      console.error('Errore nell\'espandere l\'app:', e);
    }
  }
};

// Blocca l'orientamento dell'app (disponibile dalla v6.2+)
export const lockOrientation = (): void => {
  try {
    const webApp = getTelegramWebApp();
    if (!webApp) {
      console.debug('[TELEGRAM] WebApp non disponibile per lockOrientation');
      return;
    }

    const version = webApp.version ? parseFloat(webApp.version) : 6.0;

    if (version < 6.2) {
      console.debug(`[TELEGRAM] lockOrientation non supportato nella versione ${version} (richiede 6.2+)`);
      return;
    }

    // Prova con postEvent diretto
    postTelegramEvent('web_app_setup_orientation_lock', { is_locked: true });
    console.log('[TELEGRAM] ✅ Orientamento bloccato tramite postEvent');

    // Fallback: prova con API diretta se disponibile
    const webAppAny = webApp as any;
    if (webAppAny.lockOrientation) {
      webAppAny.lockOrientation();
      console.log('[TELEGRAM] ✅ Orientamento bloccato tramite API diretta');
    }
  } catch (error) {
    console.error('[TELEGRAM] ❌ Errore nel bloccare l\'orientamento:', error);
  }
};

// Sblocca l'orientamento dell'app
export const unlockOrientation = (): void => {
  try {
    const webApp = getTelegramWebApp();
    if (!webApp) return;

    const version = webApp.version ? parseFloat(webApp.version) : 6.0;

    if (version < 6.2) {
      console.debug(`[TELEGRAM] unlockOrientation non supportato nella versione ${version} (richiede 6.2+)`);
      return;
    }

    // Prova con postEvent diretto
    postTelegramEvent('web_app_setup_orientation_lock', { is_locked: false });
    console.log('[TELEGRAM] ✅ Orientamento sbloccato tramite postEvent');

    // Fallback: prova con API diretta se disponibile
    const webAppAny = webApp as any;
    if (webAppAny.unlockOrientation) {
      webAppAny.unlockOrientation();
      console.log('[TELEGRAM] ✅ Orientamento sbloccato tramite API diretta');
    }
  } catch (error) {
    console.error('[TELEGRAM] ❌ Errore nello sbloccare l\'orientamento:', error);
  }
};

// Sistema di gestione viewport dinamico per Telegram
export const initTelegramViewportHandler = (): void => {
  const webApp = getTelegramWebApp();
  if (!webApp) return;

  console.log('[TELEGRAM VIEWPORT] Inizializzazione sistema viewport dinamico...');

  // Funzione per aggiornare le CSS variables
  const updateViewportVars = () => {
    const root = document.documentElement;

    if (webApp.viewportHeight) {
      root.style.setProperty('--telegram-viewport-height', `${webApp.viewportHeight}px`);
      console.log('[TELEGRAM VIEWPORT] Altezza aggiornata:', webApp.viewportHeight);
    }

    if (webApp.viewportStableHeight) {
      root.style.setProperty('--tg-viewport-stable-height', `${webApp.viewportStableHeight}px`);
      console.log('[TELEGRAM VIEWPORT] Altezza stabile:', webApp.viewportStableHeight);
    }

    // Forziamo un re-layout per applicare le modifiche
    const containers = document.querySelectorAll('.mobile-register-container, .telegram-register-container');
    containers.forEach(container => {
      if (container instanceof HTMLElement) {
        container.style.height = `${webApp.viewportHeight || window.innerHeight}px`;
      }
    });
  };

  // Aggiorniamo immediatamente
  updateViewportVars();

  // Ascoltiamo eventi viewport se disponibili
  if (webApp.onEvent) {
    webApp.onEvent('viewportChanged', updateViewportVars);
    webApp.onEvent('safeAreaChanged', updateViewportVars);
    webApp.onEvent('themeChanged', updateViewportVars);
    console.log('[TELEGRAM VIEWPORT] Event listeners registrati');
  }

  // Polling ridotto ogni 2 secondi per catturare cambiamenti
  const intervalId = setInterval(updateViewportVars, 2000);

  // Ottimizzazione specifica per contenuto registration form
  const optimizeRegistrationLayout = () => {
    const registrationContainers = document.querySelectorAll('.telegram-register-container, .telegram-form-container');
    registrationContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        // Rimuovi overflow per evitare scrolling indesiderato
        container.style.overflow = 'hidden';
        container.style.overflowY = 'hidden';
        container.style.touchAction = 'pan-x pan-y pinch-zoom';
      }
    });
  };

  // Applica ottimizzazione dopo un breve delay
  setTimeout(optimizeRegistrationLayout, 1000);

  // Cleanup dopo 60 secondi per evitare memory leaks
  setTimeout(() => {
    clearInterval(intervalId);
    console.log('[TELEGRAM VIEWPORT] Polling terminato dopo 60s');
  }, 60000);

  console.log('[TELEGRAM VIEWPORT] Sistema viewport dinamico inizializzato');
};

// Notifica a Telegram che l'app è pronta
export const notifyAppReady = (): void => {
  const webApp = getTelegramWebApp();
  if (webApp) {
    try {
      webApp.ready();
    } catch (e) {
      console.error('Errore nel notificare app ready:', e);
    }
  }
};

// Mantiene un riferimento al listener corrente del MainButton, per poterlo pulire
let currentMainButtonListener: Function | null = null;

// Gestisce il pulsante principale di Telegram
export const setupMainButton = (
  params: {
    text: string;
    onClick: () => void;
    color?: string;
    textColor?: string;
    isVisible?: boolean;
    isActive?: boolean;
    isProgressVisible?: boolean;
  }
): void => {
  const webApp = getTelegramWebApp();
  if (!webApp || !webApp.MainButton) return;

  try {
    const { MainButton } = webApp;
    const {
      text,
      onClick,
      color = '#9D77FF',
      textColor = '#ffffff',
      isVisible = true,
      isActive = true,
      isProgressVisible = false
    } = params;

    // Prima pulisci tutti i listener esistenti se disponibile
    try {
      if (typeof (MainButton as any).offClick === 'function') {
        (MainButton as any).offClick();
      }
    } catch (e) {
      console.log('offClick non disponibile, procedo con onClick');
    }

    // Configura le proprietà del MainButton
    MainButton.text = text;
    MainButton.color = color;
    MainButton.textColor = textColor;

    // Approccio alternativo: usa addEventListener se disponibile
    try {
      // Prova prima il metodo standard
      MainButton.onClick(onClick);
      console.log(`MainButton configurato con onClick: testo="${text}"`);
    } catch (error) {
      console.warn('onClick non funziona, provo approccio alternativo:', error);

      // Approccio alternativo: usa l'evento diretto
      try {
        const webApp = getTelegramWebApp();
        if (webApp && webApp.onEvent) {
          webApp.onEvent('mainButtonClicked', onClick);
          console.log('MainButton configurato con onEvent mainButtonClicked');
        } else {
          // Fallback: usa il metodo diretto
          (MainButton as any).onclick = onClick;
          console.log('MainButton configurato con onclick diretto');
        }
      } catch (fallbackError) {
        console.error('Tutti i metodi di configurazione MainButton falliti:', fallbackError);
      }
    }

    // Gestione avanzata delle proprietà
    try {
      // Alcuni metodi potrebbero non essere disponibili in tutte le versioni di Telegram
      // Utilizziamo any per accedere a proprietà e metodi avanzati
      const advMainButton = MainButton as any;

      // Supporto per mostrare/nascondere l'indicatore di progresso
      if (isProgressVisible && advMainButton.showProgress) {
        advMainButton.showProgress();
        console.log('Indicatore di progresso del MainButton attivato');
      } else if (!isProgressVisible && advMainButton.hideProgress) {
        advMainButton.hideProgress();
      }

      // Supporto per attivare/disattivare il pulsante
      if (isActive && advMainButton.enable) {
        advMainButton.enable();
      } else if (!isActive && advMainButton.disable) {
        advMainButton.disable();
      }
    } catch (advError) {
      console.warn('Alcune funzionalità avanzate del MainButton non sono supportate:', advError);
    }

    // Mostra o nascondi il pulsante
    if (isVisible) {
      MainButton.show();
    } else {
      MainButton.hide();
    }

    console.log(`MainButton configurato: ${text}, visibile: ${isVisible}, attivo: ${isActive}, progresso: ${isProgressVisible}`);
  } catch (e) {
    console.error('[TELEGRAM] Errore configurazione MainButton:', e);
  }
};

export const hideMainButton = (): void => {
  const webApp = getTelegramWebApp();
  if (webApp && webApp.MainButton) {
    try {
      // Cleanup del listener attuale
      if (currentMainButtonListener) {
        try {
          if (typeof (webApp.MainButton as any).offClick === 'function') {
            (webApp.MainButton as any).offClick(currentMainButtonListener);
          }
          currentMainButtonListener = null;
        } catch (e) {
          console.log('[TELEGRAM] Cleanup listener durante hide fallito:', e);
        }
      }

      const mainBtn = webApp.MainButton as any;
      if (typeof mainBtn.hide === 'function') {
        mainBtn.hide();
        console.log('[TELEGRAM] MainButton nascosto');
      }
    } catch (error) {
      console.error('[TELEGRAM] Errore nascondimento MainButton:', error);
    }
  }
};

/**
 * Ottiene i dati dell'utente Telegram dalla WebApp
 * Utilizza initDataUnsafe che è disponibile in ambienti WebApp Telegram
 * @returns Oggetto TelegramUser o null se non disponibile
 */
export const getTelegramUser = (): TelegramUser | null => {
  const webApp = getTelegramWebApp();
  console.log("Tentativo di recuperare dati utente Telegram...");

  try {
    if (!webApp) {
      console.warn("WebApp Telegram non disponibile - possibilmente non in un ambiente Telegram");
    } else {
      console.debug("WebApp Telegram trovata:", {
        version: webApp.version,
        initDataExists: !!webApp.initData,
        initDataUnsafeExists: !!webApp.initDataUnsafe
      });

      // Log dettagliato dei dati disponibili per debug
      if (webApp.initDataUnsafe) {
        console.log("DETTAGLIO DATI TELEGRAM initDataUnsafe:", {
          user: webApp.initDataUnsafe.user ? {
            id: webApp.initDataUnsafe.user.id,
            username: webApp.initDataUnsafe.user.username,
            firstName: webApp.initDataUnsafe.user.first_name,
            lastName: webApp.initDataUnsafe.user.last_name,
            idType: typeof webApp.initDataUnsafe.user.id
          } : 'NON DISPONIBILE',
          startParam: webApp.initDataUnsafe.start_param,
          queryId: webApp.initDataUnsafe.query_id
        });
      }
    }

    // Verifica approfondita per capire il problema
    if (!webApp || !webApp.initDataUnsafe || !webApp.initDataUnsafe.user) {
      // SOLO in modalità sviluppo E se siamo su Replit, restituiamo l'utente sviluppo
      if (process.env.NODE_ENV === 'development' &&
          (window.location.hostname.includes('replit.dev') || window.location.hostname.includes('replit.co'))) {
        console.log('[TelegramService] Modalità sviluppo su Replit: usando utente sviluppo');

        const timezoneData = getUserTimezone();

        // FORZA l'utilizzo dell'utente sviluppo SOLO su Replit
        return {
          id: 999999999, // ID speciale per sviluppo
          first_name: 'Sviluppo',
          last_name: '',
          username: 'sviluppo',
          language_code: 'it',
          needsRegistration: false, // L'utente sviluppo esiste già
          timezone: timezoneData.timezone,
          timezoneOffset: timezoneData.offset
        };
      }

      // Verifica se siamo in ambiente Telegram reale
      if (window.location.hostname.includes('telegram') ||
          window.location.hostname.includes('t.me') ||
          window.location.href.includes('tgwebapp=')) {
        // Siamo in una Mini App Telegram, ma i dati utente non sono disponibili
        console.log("In ambiente Telegram reale, ma i dati utente non sono disponibili");
        console.log("Questo indica un problema con l'inizializzazione di Telegram WebApp");

        // Per utenti reali di Telegram senza dati completi, proviamo a recuperare
        // almeno i dati di base per permettere il controllo username
        try {
          // Tentativo di recupero alternativo dai parametri URL o altre fonti
          const urlParams = new URLSearchParams(window.location.search);
          const userParam = urlParams.get('user');

          if (userParam) {
            const parsedUser = JSON.parse(decodeURIComponent(userParam));
            console.log("Recuperato utente da parametri URL:", parsedUser);
            return parsedUser;
          }
        } catch (parseErr) {
          console.warn("Errore nel parsing dei parametri URL:", parseErr);
        }

        // Se proprio non riusciamo a recuperare nulla, restituiamo null
        return null;
      }

      // Log dettagliato per diagnostica
      console.warn("Dati utente Telegram non disponibili", {
        webAppExists: !!webApp,
        initDataUnsafeExists: webApp ? !!webApp.initDataUnsafe : false,
        userExists: webApp && webApp.initDataUnsafe ? !!webApp.initDataUnsafe.user : false,
        initData: webApp ? webApp.initData : null
      });

      // Tentativo di recupero alternativo (solo in produzione)
      if (webApp && webApp.initData && webApp.initData.length > 0) {
        try {
          // In alcuni casi, potremmo dover analizzare manualmente initData
          const parsedData = new URLSearchParams(webApp.initData);
          const userParam = parsedData.get('user');

          if (userParam) {
            const parsedUser = JSON.parse(decodeURIComponent(userParam));
            console.log("Recuperato utente da initData:", parsedUser);
            return parsedUser;
          }
        } catch (parseErr) {
          console.error("Errore nel parsing manuale di initData:", parseErr);
        }
      }

      // Ultimo tentativo con approccio tramite window.Telegram
      try {
        // Accesso diretto all'oggetto window per ispezionare strutture alternative
        const telegramAny = (window as any).Telegram;
        if (telegramAny && telegramAny.WebApp && telegramAny.WebApp.initDataUnsafe && telegramAny.WebApp.initDataUnsafe.user) {
          const directUser = telegramAny.WebApp.initDataUnsafe.user;
          console.log("Utente recuperato con accesso diretto:", directUser);
          return directUser;
        }
      } catch (windowErr) {
        console.error("Errore nell'accesso diretto a window.Telegram:", windowErr);
      }

      return null;
    }

    // Utente trovato nel percorso standard
    const telegramUser = webApp.initDataUnsafe.user;
    console.log("Dati utente Telegram trovati:", telegramUser);

    // Assicuriamoci che l'ID sia sempre in formato numerico
    if (telegramUser && telegramUser.id) {
      // Recupera e aggiunge i dati di timezone se disponibili
      const timezoneData = getUserTimezone();
      return {
        ...telegramUser,
        id: typeof telegramUser.id === 'string' ? parseInt(telegramUser.id, 10) : telegramUser.id,
        timezone: timezoneData.timezone,
        timezoneOffset: timezoneData.offset
      };
    }

    return telegramUser;
  } catch (error) {
    console.error("Errore durante il recupero dei dati utente Telegram:", error);

    // In sviluppo, restituiamo un utente speciale che indica necessità di registrazione
    if (process.env.NODE_ENV === 'development') {
      console.log('[TelegramService] Modalità sviluppo: usando utente 137 (sviluppo)');

      const timezoneData = getUserTimezone();

      // FORZA l'utilizzo dell'utente 137 (sviluppo) in modalità sviluppo
      return {
        id: 999999999, // ID speciale per sviluppo
        first_name: 'Sviluppo',
        last_name: '',
        username: 'sviluppo',
        language_code: 'it',
        needsRegistration: false, // L'utente sviluppo esiste già
        timezone: timezoneData.timezone,
        timezoneOffset: timezoneData.offset
      };
    }

    return null;
  }
};

// Recupera i dati dell'utente Telegram (deprecato, usare getTelegramUser)
export const getUserData = (): any => {
  console.warn('getUserData è deprecato, utilizzare getTelegramUser');
  return getTelegramUser();
};

// Interfaccia per la posizione
export interface LocationData {
  latitude: number;
  longitude: number;
}

/**
 * Richiede all'utente la sua posizione tramite le API di Telegram
 * Se l'API Telegram non è disponibile, utilizza l'API di geolocalizzazione del browser
 * @returns Promise con i dati di posizione (latitudine, longitudine)
 */
export const requestLocation = (): Promise<LocationData> => {
  return new Promise((resolve, reject) => {
    // Verifichiamo se siamo in Telegram e se esiste l'API
    if (isTelegramWebApp()) {
      // Telegram ha aggiunto il supporto per requestLocation dalla versione 6.9
      // Se non disponibile, utilizzeremo un fallback
      const webApp = window.Telegram?.WebApp;
      // Dobbiamo gestire l'API in modo più flessibile perché non è completamente tipizzata
      const webAppAny = webApp as any;
      if (webAppAny && typeof webAppAny.requestLocation === 'function') {
        try {
          webAppAny.requestLocation((success: boolean, location: LocationData) => {
            if (success && location) {
              resolve(location);
            } else {
              // L'utente ha negato il permesso o c'è stato un errore
              fallbackToNavigator(resolve, reject);
            }
          });
          return;
        } catch (error) {
          console.warn('Errore in Telegram.WebApp.requestLocation:', error);
          fallbackToNavigator(resolve, reject);
          return;
        }
      } else {
        // L'API non è disponibile in questa versione, proviamo con postEvent diretto
        try {
          const win = window as any;
          if (win.TelegramWebviewProxy) {
            // In alcune versioni di Telegram potremmo usare questa tecnica
            // anche se non è documentata
            let responded = false;
            const callbackId = `loc_${Date.now()}`;

            win[callbackId] = (success: boolean, latitude: number, longitude: number) => {
              responded = true;
              if (success) {
                resolve({ latitude, longitude });
              } else {
                fallbackToNavigator(resolve, reject);
              }
              delete win[callbackId];
            };

            // Tentiamo di inviare un evento personalizzato
            win.TelegramWebviewProxy.postEvent("web_app_request_location", JSON.stringify({ callback: callbackId }));

            // Se dopo 3 secondi non abbiamo ricevuto risposta, utilizziamo il fallback
            setTimeout(() => {
              if (!responded) {
                delete win[callbackId];
                fallbackToNavigator(resolve, reject);
              }
            }, 3000);
            return;
          }
        } catch (error) {
          console.warn('Errore nel tentare evento postEvent requestLocation:', error);
        }
      }
    }

    // Se non siamo in Telegram o le API non funzionano, utilizziamo il fallback
    fallbackToNavigator(resolve, reject);
  });
};

// Funzione di fallback che utilizza l'API di geolocalizzazione del browser
const fallbackToNavigator = (
  resolve: (location: LocationData) => void,
  reject: (error: any) => void
) => {
  // In modalità sviluppo, fornisci dati di posizione simulati per testare senza il browser che blocca
  if (process.env.NODE_ENV === 'development') {
    console.log('Utilizzando posizione simulata per ambiente di sviluppo');
    // Coordinate di Milano
    resolve({
      latitude: 45.4642,
      longitude: 9.1900
    });
    return;
  }

  // In produzione, utilizziamo la geolocalizzazione del browser
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        console.error('Errore geolocalizzazione browser:', error);
        reject(new Error('Impossibile ottenere la posizione'));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  } else {
    reject(new Error('Geolocalizzazione non supportata'));
  }
};

// Helper per ottenere la timezone e l'offset dal browser
export const getUserTimezone = (): { timezone: string; offset: number } => {
  try {
    const date = new Date();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const offset = date.getTimezoneOffset(); // Offset in minuti
    console.log(`Timezone rilevata: ${timezone}, Offset: ${offset} minuti`);
    return { timezone, offset };
  } catch (error) {
    console.error("Errore nel rilevamento della timezone:", error);
    // Valori di fallback
    return { timezone: 'UTC', offset: 0 };
  }
};

// Configura e mostra il pulsante Back di Telegram
// Stack di handlers per gestire più componenti contemporaneamente
const backButtonStack: { id: string; handler: () => void }[] = [];
let currentBackButtonId: string | null = null;

// Genera un ID univoco per ogni handler
const generateBackButtonId = (): string => {
  return `bb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ─────────────────────────────────────────────────────────────────────
// IN-APP BACK BUTTON (PWA standalone)
// Fuori da Telegram non esiste il BackButton nativo. Per non duplicare la
// logica nei ~18 call site, show/hideBackButton pilotano qui uno stack in-app
// a cui si abbona <InAppBackButton/>, mostrato solo in PWA standalone.
// ─────────────────────────────────────────────────────────────────────
type InAppBackEntry = { id: string; handler: () => void };
let inAppBackStack: InAppBackEntry[] = [];
const inAppBackListeners = new Set<() => void>();
const notifyInAppBack = () => {
  inAppBackListeners.forEach((l) => { try { l(); } catch (_) {} });
};

/** True quando l'app gira come PWA installata (standalone), fuori da Telegram. */
export const isStandalonePWA = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (isTelegramWebApp()) return false;
  const mm = typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches;
  const iosStandalone = (window.navigator as any).standalone === true;
  return !!(mm || iosStandalone);
};

/** Abbona un listener ai cambi dello stack back in-app. Ritorna l'unsubscribe. */
export const subscribeInAppBack = (listener: () => void): (() => void) => {
  inAppBackListeners.add(listener);
  return () => { inAppBackListeners.delete(listener); };
};

/** True se c'è almeno un handler back in-app attivo. */
export const hasInAppBack = (): boolean => inAppBackStack.length > 0;

/** Esegue l'handler back in-app in cima allo stack. */
export const triggerInAppBack = (): void => {
  const top = inAppBackStack[inAppBackStack.length - 1];
  if (top) top.handler();
};

export const showBackButton = (onClick: () => void): string => {
  const webApp = getTelegramWebApp();
  console.log('[BackButton] showBackButton called, webApp:', !!webApp);
  if (!webApp) {
    // Fuori da Telegram: alimenta lo stack back in-app (usato dalla PWA standalone)
    const id = generateBackButtonId();
    inAppBackStack.push({ id, handler: onClick });
    notifyInAppBack();
    return id;
  }

  // Verifica versione per BackButton (disponibile dalla 6.1+)
  const version = webApp.version ? parseFloat(webApp.version) : 6.0;
  console.log('[BackButton] Telegram version:', version);

  if (version < 6.1) {
    console.log(`[BackButton] Version ${version} < 6.1, not supported`);
    return '';
  }

  if (!webApp.BackButton) {
    console.log('[BackButton] BackButton object not available');
    return '';
  }

  try {
    const { BackButton } = webApp;
    const id = generateBackButtonId();

    // Crea il nuovo handler con feedback tattile
    const handler = () => {
      // Attiva il feedback tattile se disponibile
      const webAppWithHaptics = webApp as any;
      if (version >= 6.1 && webAppWithHaptics.HapticFeedback) {
        webAppWithHaptics.HapticFeedback.impactOccurred('light');
      }

      // Esegue la funzione di callback originale
      onClick();
    };

    // Se c'è un handler precedente, rimuovilo
    if (currentBackButtonId && backButtonStack.length > 0) {
      const currentHandler = backButtonStack[backButtonStack.length - 1]?.handler;
      if (currentHandler && typeof BackButton.offClick === 'function') {
        BackButton.offClick(currentHandler);
      }
    }

    // Aggiungi il nuovo handler allo stack
    backButtonStack.push({ id, handler });
    currentBackButtonId = id;

    // Registra il nuovo handler
    BackButton.onClick(handler);

    // Mostra il pulsante
    console.log('[BackButton] isVisible before show:', BackButton.isVisible);
    if (!BackButton.isVisible) {
      console.log('[BackButton] Calling BackButton.show()');
      BackButton.show();
      console.log('[BackButton] isVisible after show:', BackButton.isVisible);
    } else {
      console.log('[BackButton] Button already visible, skipping show()');
    }

    console.log(`[BackButton] Handler registrato: ${id}, stack size: ${backButtonStack.length}`);
    return id;
  } catch (e) {
    console.debug('BackButton non configurabile in questa versione:', e);
    return '';
  }
};

// Nasconde il pulsante Back di Telegram specifico (RICHIEDE ID!)
export const hideBackButton = (id?: string): void => {
  console.log('[BackButton] hideBackButton called, id:', id, 'stack size:', backButtonStack.length);

  const webApp = getTelegramWebApp();
  if (!webApp) {
    // Fuori da Telegram: rimuovi dallo stack back in-app
    if (id) {
      const idx = inAppBackStack.findIndex((e) => e.id === id);
      if (idx !== -1) inAppBackStack.splice(idx, 1);
    } else {
      inAppBackStack.pop();
    }
    notifyInAppBack();
    return;
  }

  // Verifica versione per BackButton (disponibile dalla 6.1+)
  const version = webApp.version ? parseFloat(webApp.version) : 6.0;

  if (version < 6.1) {
    console.debug(`BackButton non supportato nella versione ${version} (richiede 6.1+)`);
    return;
  }

  if (webApp.BackButton) {
    try {
      const { BackButton } = webApp;

      // Trova l'handler da rimuovere
      let handlerToRemove: (() => void) | null = null;
      let removedIndex = -1;

      if (id) {
        // Rimuovi l'handler specifico
        removedIndex = backButtonStack.findIndex(item => item.id === id);
        if (removedIndex !== -1) {
          handlerToRemove = backButtonStack[removedIndex].handler;
          backButtonStack.splice(removedIndex, 1);
          console.log(`[BackButton] Handler rimosso: ${id}, stack size: ${backButtonStack.length}`);
        }
      } else {
        // Rimuovi l'ultimo handler
        if (backButtonStack.length > 0) {
          const item = backButtonStack.pop();
          if (item) {
            handlerToRemove = item.handler;
            console.log(`[BackButton] Handler rimosso (ultimo): ${item.id}, stack size: ${backButtonStack.length}`);
          }
        }
      }

      // Rimuovi l'handler dal BackButton
      if (handlerToRemove && typeof BackButton.offClick === 'function') {
        BackButton.offClick(handlerToRemove);
      }

      // Se ci sono ancora handler nello stack, ripristina il precedente
      if (backButtonStack.length > 0) {
        const previousItem = backButtonStack[backButtonStack.length - 1];
        currentBackButtonId = previousItem.id;
        BackButton.onClick(previousItem.handler);
        console.log(`[BackButton] Handler ripristinato: ${previousItem.id}`);
        // Assicurati che il pulsante sia visibile
        if (!BackButton.isVisible) {
          BackButton.show();
        }
      } else {
        // Nessun handler rimasto, nascondi il pulsante
        currentBackButtonId = null;
        BackButton.hide();
        console.log('[BackButton] Nessun handler rimasto, pulsante nascosto');
      }
    } catch (e) {
      console.debug('Errore nel nascondere il BackButton:', e);
    }
  }
};

/**
 * Mostra un alert nativo di Telegram (notifica push semplice)
 * @param message Il messaggio da mostrare
 */
export const showAlert = (message: string): void => {
  const webApp = getTelegramWebApp();
  if (!webApp) {
    // Fallback per browser normali
    alert(message);
    return;
  }

  try {
    const webAppAny = webApp as any;
    if (webAppAny.showAlert) {
      webAppAny.showAlert(message);
      console.log('[TelegramService] Alert mostrato:', message);
    } else {
      // Fallback
      alert(message);
    }
  } catch (error) {
    console.error('[TelegramService] Errore showAlert:', error);
    alert(message);
  }
};

/**
 * Mostra un popup nativo di Telegram con pulsanti personalizzati
 * @param params Parametri del popup
 */
export const showPopup = (params: {
  title?: string;
  message: string;
  buttons?: Array<{
    id?: string;
    type?: 'default' | 'ok' | 'close' | 'cancel' | 'destructive';
    text?: string;
  }>;
}): Promise<string | null> => {
  return new Promise((resolve) => {
    const webApp = getTelegramWebApp();
    if (!webApp) {
      // Fallback per browser normali
      const result = confirm(`${params.title || ''}\n${params.message}`);
      resolve(result ? 'ok' : 'cancel');
      return;
    }

    try {
      const webAppAny = webApp as any;
      if (webAppAny.showPopup) {
        webAppAny.showPopup(params, (buttonId: string) => {
          console.log('[TelegramService] Popup risposta:', buttonId);
          resolve(buttonId);
        });
      } else {
        // Fallback
        const result = confirm(`${params.title || ''}\n${params.message}`);
        resolve(result ? 'ok' : 'cancel');
      }
    } catch (error) {
      console.error('[TelegramService] Errore showPopup:', error);
      const result = confirm(`${params.title || ''}\n${params.message}`);
      resolve(result ? 'ok' : 'cancel');
    }
  });
};

/**
 * Mostra una notifica di successo
 */
export const showSuccessNotification = (message: string): void => {
  const webApp = getTelegramWebApp();
  if (webApp) {
    try {
      const webAppAny = webApp as any;
      // Attiva feedback tattile di successo
      if (webAppAny.HapticFeedback) {
        webAppAny.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.warn('[TelegramService] Feedback tattile non disponibile:', error);
    }
  }

  showAlert(`✅ ${message}`);
};

/**
 * Mostra una notifica di errore
 */
export const showErrorNotification = (message: string): void => {
  const webApp = getTelegramWebApp();
  if (webApp) {
    try {
      const webAppAny = webApp as any;
      // Attiva feedback tattile di errore
      if (webAppAny.HapticFeedback) {
        webAppAny.HapticFeedback.notificationOccurred('error');
      }
    } catch (error) {
      console.warn('[TelegramService] Feedback tattile non disponibile:', error);
    }
  }

  showAlert(`❌ ${message}`);
};

/**
 * Mostra una notifica di avviso
 */
export const showWarningNotification = (message: string): void => {
  const webApp = getTelegramWebApp();
  if (webApp) {
    try {
      const webAppAny = webApp as any;
      // Attiva feedback tattile di warning
      if (webAppAny.HapticFeedback) {
        webAppAny.HapticFeedback.notificationOccurred('warning');
      }
    } catch (error) {
      console.warn('[TelegramService] Feedback tattile non disponibile:', error);
    }
  }

  showAlert(`⚠️ ${message}`);
};

/**
 * Mostra una notifica per nuove visualizzazioni del profilo
 */
export const showNewViewsNotification = (viewerUsername: string, viewerPhotoUrl?: string): void => {
  console.log('[TELEGRAM SERVICE] 🔔 Mostrando notifica nuova visualizzazione:', viewerUsername);

  if (!isTelegramWebApp()) {
    console.log('[TELEGRAM SERVICE] ⚠️ Non in ambiente Telegram, skip notifica');
    return;
  }

  const tg = getTelegramWebApp();
  if (!tg?.showPopup) {
    console.log('[TELEGRAM SERVICE] ⚠️ showPopup non disponibile');
    return;
  }

  // Creare un elemento DOM temporaneo per il banner personalizzato
  const banner = document.createElement('div');
  banner.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 999999;
    animation: slideDown 0.3s ease-out;
  `;

  // Container per avatar + icona occhi
  const avatarContainer = document.createElement('div');
  avatarContainer.style.cssText = `
    position: relative;
    width: 64px;
    height: 64px;
  `;

  // Avatar dell'utente circolare
  const avatar = document.createElement('img');
  avatar.src = viewerPhotoUrl || 'https://em-content.zobj.net/source/apple/419/busts-in-silhouette_1f465.png';
  avatar.style.cssText = `
    width: 64px;
    height: 64px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
  `;

  // Emoji occhi animata - posizionata in basso a destra sull'avatar
  const eyesEmoji = document.createElement('img');
  eyesEmoji.src = 'https://em-content.zobj.net/source/telegram/386/eyes_1f440.webp';
  eyesEmoji.style.cssText = `
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 28px;
    height: 28px;
    background: white;
    border-radius: 50%;
    padding: 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  `;

  // Componi il banner
  avatarContainer.appendChild(avatar);
  avatarContainer.appendChild(eyesEmoji);
  banner.appendChild(avatarContainer);

  // Aggiungi al DOM
  document.body.appendChild(banner);

  // Rimuovi dopo 3 secondi
  setTimeout(() => {
    banner.style.animation = 'slideUp 0.3s ease-out';
    setTimeout(() => {
      banner.remove();
    }, 300);
  }, 3000);

  // Aggiungi le animazioni CSS se non esistono già
  if (!document.getElementById('notification-animations')) {
    const style = document.createElement('style');
    style.id = 'notification-animations';
    style.textContent = `
      @keyframes slideDown {
        from {
          transform: translateX(-50%) translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
        }
      }
      @keyframes slideUp {
        from {
          transform: translateX(-50%) translateY(0);
          opacity: 1;
        }
        to {
          transform: translateX(-50%) translateY(-100%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  console.log('[TELEGRAM SERVICE] ✅ Banner notifica mostrato');
};

export const telegramService = {
  isTelegramWebApp,
  getTelegramInitData,
  getTelegramUser,
  expandApp,
  notifyAppReady,
  setupMainButton,
  hideMainButton,
  showBackButton,
  hideBackButton,
  getUserData,
  requestFullScreen,
  disableVerticalSwipe,
  lockOrientation,
  unlockOrientation,
  requestLocation,
  showAlert,
  showPopup,
  showSuccessNotification,
  showErrorNotification,
  showWarningNotification,
  showNewViewsNotification
};

export default telegramService;